import validators, { ValidationType } from '../utils/validators';
import { format } from 'date-fns';
import { getUTCTimeFromDateString } from '../utils/dates';

type FieldNameRootAndIndex = {
  fieldNameRoot?: string;
  index?: any;
};

export const INDEXED_FIELD_NAME_REGEX = /^([A-Za-z_]+)(\d*)_([A-Za-z]+)$/;
const DEFAULT_ERROR_MESSAGE = 'Enter a valid value';

export enum FieldDataType {
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  date = 'date',
  currency = 'currency',
}

class Field {
  /**
   * Given a fieldName return field name root and index if it's an indexed field.
   * e.g.: tenant0_email => { index: 0, fieldNameRoot: 'tenant_email'}
   * If a field isn't an indexed field, original fieldName is returned with an index of null
   * @param fieldName
   */
  static getFieldNameRootAndIndex(fieldName): FieldNameRootAndIndex {
    const fieldNameGroups = fieldName.match(INDEXED_FIELD_NAME_REGEX);

    let fieldNameRoot = fieldName;
    let index: number;

    if (fieldNameGroups != null && fieldNameGroups[2] !== '') {
      fieldNameRoot = `${fieldNameGroups[1]}_${fieldNameGroups[3]}`;
      index = Number(fieldNameGroups[2]);
    }

    return { index, fieldNameRoot };
  }

  defaultValue: any;

  enteredValue: any;

  fieldName: string;

  dataType: FieldDataType;

  hasError: boolean;

  initialParams: any;

  errorMessage: string;

  optional: boolean;

  validation: ValidationType;

  hasFieldChanged: boolean;

  disabled: boolean;

  customValidator: (value: any) => boolean;

  constructor(params: any) {
    this.fieldName = params.fieldName || '';
    this.dataType = params.dataType;
    this.optional = params.optional;
    this.validation = params.validation;
    this.defaultValue = params.defaultValue;
    this.initialParams = params;
    this.customValidator = params.customValidator;
    this.setEnteredValueToDefault(params.enteredValue);
    this.disabled = params.disabled;
  }

  isDefined = () => this.enteredValue !== '' && this.enteredValue != null;

  setEnteredValueToDefault = (enteredValueOverride = null) => {
    let enteredValue = enteredValueOverride || this.defaultValue || '';

    switch (this.dataType) {
      case FieldDataType.string:
        break;
      case FieldDataType.number:
        enteredValue = enteredValue !== '' ? Number(enteredValue) : null;
        break;
      case FieldDataType.boolean:
        enteredValue = enteredValue === '' ? null : enteredValue.toString();
        enteredValue = JSON.parse(enteredValue);
        break;
      case FieldDataType.currency:
        // The API gives us back currency in cents, so we'll convert it to dollars here
        enteredValue = enteredValue !== '' && enteredValue != null ? String(Number(enteredValue) / 100) : '';
        break;
      case FieldDataType.date:
        // New code will take the local date (including legacy dates before the fix) and get the UTC year, month and date.
        // Anything to the west of UTC and to the east of the Interional Date Line will be unaffected, but those to the east
        // may be. (Very few Zillow leases outside the US.)
        if (enteredValue) {
          const localDate = new Date(Number(enteredValue));
          const utcDate = new Date(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate());
          enteredValue = format(utcDate, 'MMMM D, YYYY');
        } else {
          enteredValue = null;
        }
        // Old code converted based on time zone of user's date.
        //enteredValue = enteredValue ? format(parse(Number(enteredValue)), 'MMMM D, YYYY') : null;
        break;
      default:
        break;
    }

    this.setEnteredValue(enteredValue, true);
  };

  getSeleniumLeaseDataJson(): string {
    return JSON.stringify({
      fieldName: this.fieldName,
      fieldValue: this.getJsonValue(),
    });
  }

  getJsonValue = (): string => {
    let fieldValue = this.enteredValue == null ? '' : String(this.enteredValue);
    switch (this.dataType) {
      case FieldDataType.string:
        break;
      case FieldDataType.number:
        break;
      case FieldDataType.boolean:
        fieldValue = fieldValue === '' ? '' : String(fieldValue === 'true');
        break;
      case FieldDataType.currency:
        fieldValue = fieldValue === '' ? '' : String(Field.getValueInCents(fieldValue));
        break;
      case FieldDataType.date:
        fieldValue = getUTCTimeFromDateString(this.enteredValue);
        break;
      default:
        console.error(`Could not resolve dataType ${this.dataType} for field ${this.fieldName}`);
        break;
    }

    return fieldValue;
  };

  getFieldNameRootAndIndex(): FieldNameRootAndIndex {
    return Field.getFieldNameRootAndIndex(this.fieldName);
  }

  /**
   * Given a string representation of currency, return the equivalent value in cents as an integer
   * Note we're calculating this manually rather than casting using Number() to avoid any possible
   * rounding errors
   *
   * @param {string} value
   * @returns {number}
   */
  static getValueInCents(value: string): number {
    let parsedValue = String(value);

    // Strip negative sign if it's there
    const negativeSplit = parsedValue.split('-');
    const isNegative = negativeSplit.length === 2;
    parsedValue = negativeSplit[1] || negativeSplit[0];

    // Remove dollar sign
    parsedValue = parsedValue.replace('$', '');
    // Remove commas
    parsedValue = parsedValue.replace(/,/g, '');

    const splitParsedValue = parsedValue.split('.');
    const dollarsInCents = Number(splitParsedValue[0]) * 100;
    const cents = Number(splitParsedValue[1] || 0);

    return isNegative ? 0 - dollarsInCents - cents : dollarsInCents + cents;
  }

  static getIndexedFieldName(fieldName: string, index: number): string {
    const groups = fieldName.match(INDEXED_FIELD_NAME_REGEX);

    if (groups) {
      return `${groups[1]}${index}_${groups[3]}`;
    }

    return `${fieldName}_${index}`;
  }

  setEnteredValue(enteredValue: any, isInitialValue?: boolean) {
    this.enteredValue = enteredValue;
    this.hasError = false;

    if (this.enteredValue && this.validation === ValidationType.EmailValidator) {
      this.enteredValue = this.enteredValue.trim();
    }

    if (!isInitialValue) {
      this.hasFieldChanged = true;
    }

    const hasEnteredValue = enteredValue !== '' && enteredValue != null;

    const isString = typeof enteredValue === 'string' || enteredValue instanceof String;
    const valueToValidate = isString ? this.enteredValue.trim() : enteredValue;

    const validator = validators.getValidator(this.validation);
    if (validator) {
      this.hasError = hasEnteredValue && !validator.isValid(valueToValidate);
      this.hasError && this.setErrorMessage(validator.getError(valueToValidate));
    }

    if (!this.hasError && this.customValidator) {
      this.hasError = hasEnteredValue && !this.customValidator(valueToValidate);
    }
  }

  setErrorMessage(message: string) {
    this.errorMessage = message || DEFAULT_ERROR_MESSAGE;
  }

  getErrorMessage() {
    return this.showOnError(this.errorMessage);
  }

  showOnError = (message): string => {
    if (this.hasError) {
      return message;
    }

    return null;
  };

  hasChanged = (): boolean => this.hasFieldChanged;
}

export default Field;
